import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { NAV_ITEMS, type NavItem } from '../app/navigation';
import { BrandMark } from './BrandMark';
import { CommandPalette } from './CommandPalette';
import { ResetCountdown } from './ResetCountdown';
import { UserMenu } from './UserMenu';

/** Renders a `NavItem.glyph` whether it's a plain dingbat character or a bespoke icon component. */
function NavGlyph({ glyph }: { glyph: NavItem['glyph'] }) {
  if (typeof glyph === 'string') {
    return <>{glyph}</>;
  }
  const Icon = glyph;
  return <Icon />;
}

const desktopLinkClasses = (isActive: boolean) =>
  [
    'flex min-h-tap items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-sm font-semibold text-text-muted transition-[color,background-color,border-color,transform] duration-150 ease-out hover:translate-x-0.5 hover:bg-bg-elevated-hover hover:text-text',
    isActive
      ? 'border-accent-border bg-gradient-to-r from-accent-dim to-transparent text-text'
      : '',
  ].join(' ');

const desktopGlyphClasses = (isActive: boolean) =>
  ['w-5 text-center text-base', isActive ? 'text-accent' : 'text-text-faint'].join(' ');

const mobileLinkClasses = (isActive: boolean) =>
  [
    'relative flex min-h-tap min-w-tap flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[0.6875rem] font-semibold transition-colors duration-150',
    isActive ? 'text-accent' : 'text-text-faint',
  ].join(' ');

/**
 * Responsive shell (product requirement, not a nice-to-have):
 * - >= md: persistent left sidebar navigation.
 * - < md: bottom tab bar, with body padding so content never hides behind it.
 * All tap targets are at least 44px (`min-h-tap` / `min-w-tap` = 2.75rem).
 *
 * "Maple Night" (docs/DESIGN.md): the shell background is a lantern-glow radial gradient derived
 * from the user's accent, so it re-tints live with the accent picker on /settings.
 */
export function AppShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="shell-grain flex min-h-full flex-col bg-bg text-text"
      style={{
        backgroundImage:
          'radial-gradient(720px 520px at 75% -10%, var(--shell-glow), transparent 70%)',
      }}
    >
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {/* REV14: fully hidden `:` command palette — no visual affordance, keydown is the only
          entry point. Mounted once here so it's available on every authed page. */}
      <CommandPalette />

      <header className="sticky top-0 z-20 border-b border-border bg-bg-elevated/90 backdrop-blur-md">
        <div className="flex min-h-tap w-full items-center justify-between gap-4 px-4 py-2 sm:px-6 lg:px-8">
          {/* Three countdown columns don't fit next to the full wordmark at 375px — the phone
              header keeps just the leaf. */}
          <span className="sm:hidden">
            <BrandMark iconOnly />
          </span>
          <span className="hidden sm:inline-flex">
            <BrandMark />
          </span>
          <div className="flex items-center gap-3">
            <ResetCountdown />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Full-width shell (docs/DESIGN.md "Layout", 2026-08-16): no centered max-w-* container —
          the rail is a fixed-width column and this pane fills every remaining pixel, `min-w-0` so
          dense grids (checklist, boss matrix) can scroll inside themselves instead of blowing out
          the pane. Breathing room comes from the pane's own padding, not a width cap. */}
      <div className="flex w-full flex-1 gap-6 px-4 sm:px-6 lg:px-8">
        <nav
          aria-label="Main navigation"
          className="hidden w-52 shrink-0 flex-col gap-1 py-6 md:flex"
          style={{ viewTransitionName: 'shell-rail' }}
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              viewTransition
              className={({ isActive }) => desktopLinkClasses(isActive)}
            >
              {({ isActive }) => (
                <>
                  <span aria-hidden="true" className={desktopGlyphClasses(isActive)}>
                    <NavGlyph glyph={item.glyph} />
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 pt-6 outline-none">
          {children}
        </main>
      </div>

      {/* The bottom padding that used to live on <main> moved here, so the footer sits below the
          content instead of after 6rem of empty space — the clearance for the fixed mobile tab bar
          is unchanged. */}
      <footer className="w-full px-4 pb-24 pt-8 sm:px-6 md:pb-10 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border pt-4 text-xs text-text-faint">
          <span>Fan-made. Not affiliated with Nexon.</span>
          <Link
            to="/privacy"
            className="rounded transition-colors hover:text-text-muted focus-visible:text-text-muted"
          >
            Privacy
          </Link>
        </div>
      </footer>

      <nav
        aria-label="Mobile navigation"
        className="fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-bg-elevated/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
        style={{ viewTransitionName: 'shell-tabbar' }}
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            viewTransition
            className={({ isActive }) => mobileLinkClasses(isActive)}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute top-1 size-1.5 rounded-full bg-accent"
                    style={{ boxShadow: '0 0 6px 1px var(--accent)' }}
                  />
                )}
                <span aria-hidden="true" className="text-lg leading-none">
                  <NavGlyph glyph={item.glyph} />
                </span>
                {item.shortLabel}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
